// Capa anticorrupción de Cartera: traduce el wire de `GET /backoffice/credits` a la fila de la tabla.
// Lo que el silo no resolvió queda AUSENTE con su motivo al lado, nunca vacío ni como «no existe».
import type {
    LoanCreditPortfolioDevice,
    LoanCreditPortfolioItem,
} from '@/api/services/loan-credit';
import {
    saleCustomerName,
    saleCustomerNameIsResolved,
    saleCustomerNote,
    saleProductBrand,
    saleProductLabel,
    saleProductNote,
    saleStoreName,
    saleStoreNameIsResolved,
    saleStoreNote,
} from '../../saleContext';
import { deviceLockStateOf } from '../../installments/logic';
import type { LockEvidence } from '../../lockPhase';
import type { PortfolioLockState, PortfolioRow } from '../types';

/**
 * El bloqueo del equipo, tal como lo resolvió el silo. Ningún motivo de «no se sabe» se aplasta a
 * `no-device`; el bloqueo programado a futuro todavía no entra y cuenta como sin bloqueo.
 */
export function resolveLockState(
    device: LoanCreditPortfolioDevice | undefined,
    nowMs: number = Date.now(),
): PortfolioLockState {
    switch (device?.status) {
        case 'RESOLVED':
            return deviceLockStateOf(device, nowMs) === 'locked' ? 'locked' : 'unlocked';
        case 'NOT_ENROLLED':
            return 'no-device';
        case 'NO_PERMISSION':
            return 'no-permission';
        case 'UNREADABLE':
            return 'unknown';
        // Un estado que el silo agregue después, o el bloque que todavía no publica: no se sabe.
        default:
            return 'unknown';
    }
}

/** Los campos del candado de un expediente leído; `undefined` si no se leyó. */
function lockEvidenceOf(device: LoanCreditPortfolioDevice | undefined): LockEvidence | undefined {
    if (device?.status !== 'RESOLVED') return undefined;
    const { mdmStatus, mdmConfirmed, lockRefutedByProvider, currentLockScheduledFor, unlockedAt } =
        device;
    return Object.fromEntries(
        Object.entries({
            mdmStatus,
            mdmConfirmed,
            lockRefutedByProvider,
            currentLockScheduledFor,
            unlockedAt,
        }).filter(([, value]) => value !== undefined),
    );
}

/**
 * Un crédito del listado, como fila de la tabla. Cliente, equipo y tienda salen del contexto de
 * venta, y cada uno lleva su motivo: las TRES lecturas fallan por separado. El bloqueo viene ya
 * resuelto en `item.device`.
 */
export function toPortfolioRow(item: LoanCreditPortfolioItem): PortfolioRow {
    const sale = item.saleContext;
    const customerName = saleCustomerName(sale);
    const productLabel = saleProductLabel(sale);
    const productBrand = saleProductBrand(sale);
    const storeName = saleStoreName(sale);
    const lockEvidence = lockEvidenceOf(item.device);
    return {
        creditId: item.creditId,
        borrowerId: item.borrowerId,
        status: item.status,
        clientLevel: item.clientLevelAtOrigination,
        ...(customerName === undefined ? {} : { customerName }),
        customerNameResolved: saleCustomerNameIsResolved(sale),
        customerNote: saleCustomerNote(sale),
        weeklyPaymentWithIvaCents: item.weeklyPaymentWithIvaCents,
        paidInstallments: item.paidInstallments,
        termWeeks: item.termWeeks,
        outstandingBalanceCents: item.outstandingBalanceCents,
        ...(item.totalDebtCents === undefined ? {} : { totalDebtCents: item.totalDebtCents }),
        arrearsDays: item.overdueDays,
        lockState: resolveLockState(item.device),
        ...(lockEvidence ? { lockEvidence } : {}),
        imei: item.imei,
        createdAt: item.createdAt,
        ...(productLabel === undefined ? {} : { productLabel }),
        ...(productBrand === undefined ? {} : { productBrand }),
        productNote: saleProductNote(sale),
        ...(storeName === undefined ? {} : { storeName }),
        storeNameResolved: saleStoreNameIsResolved(sale),
        storeNote: saleStoreNote(sale),
    };
}
